import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type {
  TagRequest,
  TagRequestStatusFilter,
  TagRequestsParams,
  TagRequestsResponse,
} from '@/client/types/tag-requests';

export const TAG_REQUESTS_BASE = '/api/admin/tag-requests';

export const tagRequestKeys = {
  all: ['tag-requests-admin'] as const,
  lists: () => [...tagRequestKeys.all, 'list'] as const,
  list: (params: TagRequestsParams) => [...tagRequestKeys.lists(), params] as const,
  detail: (etrid: number) => [...tagRequestKeys.all, 'detail', etrid] as const,
};

const STATUS_QUERY: Record<TagRequestStatusFilter, string | undefined> = {
  open: 'submitted,under_review',
  approved: 'approved',
  partially_approved: 'partially_approved',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
  all: undefined,
};

// Admin routes answer with bare `jsonify`; tolerate a `{status, data}` envelope too.
export const unwrap = (raw: unknown): any =>
  raw != null && typeof raw === 'object' && 'data' in (raw as object) && 'status' in (raw as object)
    ? (raw as { data: unknown }).data
    : raw;

const normalizeList = (raw: unknown): TagRequestsResponse => {
  const payload = unwrap(raw) ?? {};
  const requests: TagRequest[] = Array.isArray(payload.requests) ? payload.requests : [];
  return {
    requests,
    total: Number(payload.total ?? requests.length) || 0,
    page: Number(payload.page ?? 1) || 1,
    page_size: Number(payload.page_size ?? 25) || 25,
  };
};

const toQuery = ({ status, ...rest }: TagRequestsParams): string => {
  const qs = new URLSearchParams();
  const statusParam = STATUS_QUERY[status];
  if (statusParam) qs.set('status', statusParam);
  Object.entries(rest).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
};

export const useTagRequestsQuery = (params: TagRequestsParams) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: tagRequestKeys.list(params),
    queryFn: withErrorHandling(
      async () => normalizeList(await request<unknown>(`${TAG_REQUESTS_BASE}${toQuery(params)}`)),
      'Could not load tag requests',
    ),
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useTagRequestQuery = (etrid: number | null) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: tagRequestKeys.detail(etrid ?? 0),
    queryFn: withErrorHandling(
      async () => (unwrap(await request<unknown>(`${TAG_REQUESTS_BASE}/${etrid}`)) ?? {}) as TagRequest,
      'Could not load this tag request',
    ),
    enabled: Boolean(etrid),
    // Reviewers act on what they see; a 5-minute-old copy would hide another admin's decision.
    staleTime: 0,
    gcTime: DEFAULT_GC_TIME,
  });
};
